import { useQuery } from "@tanstack/react-query";
import { useLingui } from "@lingui/react";
import { patientIntakeApi } from "@/lib/patientIntakeApi";

/**
 * React hook to fetch and cache specialties from the backend API
 * Filters out dental/dentistry specialties client-side as backup
 */
export function useSpecialties(email?: string, isDev: boolean = true) {
  const { i18n } = useLingui();
  const requestedLanguage = i18n.locale?.toLowerCase().startsWith("es")
    ? "es"
    : "en";

  const { data, isLoading, error } = useQuery({
    queryKey: ["patient-intake-specialties", email, isDev, requestedLanguage],
    queryFn: async () => {
      const response = await patientIntakeApi.getSpecialties(
        email,
        isDev,
        requestedLanguage,
      );
      const specialties = response.data ? [...response.data] : [];

      // Render only API data; dedupe by slug in case backend sends duplicates.
      const specialtyBySlug = new Map<string, (typeof specialties)[number]>();
      specialties.forEach((specialty) => {
        const existing = specialtyBySlug.get(specialty.slug);
        if (!existing || specialty.display_order < existing.display_order) {
          specialtyBySlug.set(specialty.slug, specialty);
        }
      });

      return Array.from(specialtyBySlug.values()).sort(
        (a, b) => a.display_order - b.display_order,
      );
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  return {
    specialties: data ?? [],
    isLoading,
    error,
  };
}

